/**
 * rrweb session-replay player (L3 capture).
 *
 * `rrweb` is heavy and only needed when an L3 replay attachment exists, so it is
 * loaded via a *fully dynamic* import behind `@vite-ignore` — this keeps it out
 * of the dashboard bundle and out of the build graph (the dashboard does not
 * hard-depend on rrweb). If the module can't be resolved at runtime, or the
 * ticket has no replay, the component degrades to a friendly placeholder.
 *
 * Flow: fetch the replay attachment JSON (an array of rrweb events) from its
 * short-TTL ticketed URL, then mount an rrweb `Replayer` into a container.
 *
 * Visual: the Claude Design `.replay` card — a dark 16:9 stage with a live
 * "SESSION REPLAY" badge, over a `.replay-ctrl` row carrying a round primary
 * play button and the controls.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { Icon } from './ui/icons.js';
import { cn } from './ui/cn.js';

export interface ReplayPlayerProps {
  /** Ticketed GET URL of the replay events JSON (rrweb event array). */
  url: string | undefined;
}

type ReplayerLike = {
  play: () => void;
  pause: () => void;
};

/** Dynamically load rrweb's Replayer, or null if unavailable. */
async function loadReplayer(): Promise<
  | (new (events: unknown[], opts: { root: HTMLElement; skipInactive?: boolean }) => ReplayerLike)
  | null
> {
  try {
    // Variable + @vite-ignore so the bundler does not try to resolve rrweb at
    // build time (it is an optional, lazily-available dependency).
    const moduleName = 'rrweb';
    const mod = (await import(/* @vite-ignore */ moduleName)) as {
      Replayer?: new (
        events: unknown[],
        opts: { root: HTMLElement; skipInactive?: boolean },
      ) => ReplayerLike;
    };
    return mod.Replayer ?? null;
  } catch {
    return null;
  }
}

export function ReplayPlayer({ url }: ReplayPlayerProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'unavailable' | 'empty'>(
    url ? 'loading' : 'empty',
  );
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef<ReplayerLike | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setState('empty');
      return;
    }
    setState('loading');

    (async () => {
      const [Replayer, events] = await Promise.all([
        loadReplayer(),
        fetch(url)
          .then((r) => (r.ok ? (r.json() as Promise<unknown[]>) : []))
          .catch(() => [] as unknown[]),
      ]);
      if (cancelled) return;
      if (!Replayer) {
        setState('unavailable');
        return;
      }
      if (!Array.isArray(events) || events.length === 0) {
        setState('empty');
        return;
      }
      const root = containerRef.current;
      if (!root) return;
      root.innerHTML = '';
      try {
        playerRef.current = new Replayer(events, { root, skipInactive: true });
        setState('ready');
      } catch {
        setState('unavailable');
      }
    })();

    return () => {
      cancelled = true;
      playerRef.current?.pause();
      playerRef.current = null;
    };
  }, [url]);

  const ready = state === 'ready';
  const play = (): void => {
    playerRef.current?.play();
    setPlaying(true);
  };
  const pause = (): void => {
    playerRef.current?.pause();
    setPlaying(false);
  };

  const note =
    state === 'loading'
      ? 'Loading replay…'
      : state === 'empty'
        ? 'No session replay captured.'
        : state === 'unavailable'
          ? 'Replay player unavailable in this build.'
          : null;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      {/* Stage */}
      <div className="relative aspect-video overflow-hidden bg-[#0d0e12]">
        <div className="absolute left-3 top-[11px] z-[5] inline-flex items-center gap-1.5 rounded-full bg-black/50 px-[9px] py-1 text-[10.5px] font-bold tracking-[0.04em] text-white backdrop-blur-sm">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full',
              ready
                ? 'bg-danger shadow-[0_0_7px_var(--hl-danger)]'
                : 'bg-faint',
            )}
          />
          SESSION REPLAY
        </div>
        <div
          ref={containerRef}
          data-state={state}
          className="absolute inset-0 grid place-items-center overflow-hidden [&_iframe]:h-full [&_iframe]:w-full"
        >
          {note ? (
            <p className="px-4 text-center text-[12.5px] text-white/55">{note}</p>
          ) : null}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 border-t border-border px-3.5 py-[11px]">
        <button
          type="button"
          aria-label={playing ? 'Pause' : 'Play'}
          className={cn(
            'grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-full bg-primary text-primary-ink transition [&_svg]:size-[15px]',
            'hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            'disabled:cursor-default disabled:opacity-45 disabled:brightness-100',
          )}
          disabled={!ready}
          onClick={() => (playing ? pause() : play())}
        >
          {playing ? <Icon.pause /> : <Icon.play />}
        </button>
        <div className="h-[5px] flex-1 rounded-full bg-surface-3" />
        <button
          type="button"
          className="rounded-sm border border-border bg-surface-2 px-2 py-1 text-[12px] font-semibold text-muted transition hover:border-strong hover:text-text disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          disabled={!ready}
          onClick={() => (playing ? pause() : play())}
        >
          {playing ? 'Pause' : 'Play'}
        </button>
      </div>
    </div>
  );
}
